/**
 * QuickCreate - Document-First Verification Flow
 *
 * A fast-track flow for experienced contractors who know their permit type.
 * 3 steps:
 *   1. Quick Setup: County, permit type, optional address
 *   2. Document Upload: Drag-drop with auto-categorization
 *   3. Verification Results: Pass/fail with actionable next steps
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Check, FileUp, ClipboardCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QuickSetupForm } from '@/components/quick-create/QuickSetupForm';
import { DocumentDropZone } from '@/components/quick-create/DocumentDropZone';
import { VerificationResults } from '@/components/quick-create/VerificationResults';

// Step definitions
const STEPS = [
  { id: 1, name: 'Setup', icon: ClipboardCheck },
  { id: 2, name: 'Documents', icon: FileUp },
  { id: 3, name: 'Verify', icon: Check },
] as const;

export type QuickCreateStep = 1 | 2 | 3;

// Draft job data collected in Step 1
export interface DraftJobData {
  jobId: string;
  title: string;
  county: string;
  city?: string;
  streetAddress?: string;
  zipCode?: string;
  permitType: string;
  permitTypeName: string;
  jurisdictionId: number | null;
  jurisdictionCode: string | null;
  jurisdictionName: string | null;
}

// Document uploaded in Step 2
export interface UploadedDocument {
  id: string;
  name: string;
  category: string;
  categoryLabel: string;
  size: number;
  url: string;
  uploadedAt: Date;
  source: 'upload' | 'vault';
}

export default function QuickCreate() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<QuickCreateStep>(1);
  const [contractorId, setContractorId] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Data collected through the flow
  const [draftJob, setDraftJob] = useState<DraftJobData | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);

  // Auth check
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        // Get contractorId from user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const cId = userData?.contractorId || userData?.orgId || user.uid;
        setContractorId(cId);
        setIsLoading(false);
      } else {
        navigate('/auth');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Step navigation handlers
  const handleSetupComplete = (data: DraftJobData) => {
    setDraftJob(data);
    setCurrentStep(2);
  };

  const handleDocumentsComplete = (documents: UploadedDocument[]) => {
    setUploadedDocuments(documents);
    setCurrentStep(3);
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/permits');
    } else {
      setCurrentStep((prev) => (prev - 1) as QuickCreateStep);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-30">
        <div className="flex items-center gap-4 h-14 px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Quick Create</h1>
            <p className="text-xs text-muted-foreground">Document-First Verification</p>
          </div>
        </div>

        {/* Step Progress */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              const StepIcon = step.icon;

              return (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors',
                        isCompleted
                          ? 'bg-primary border-primary text-primary-foreground'
                          : isActive
                          ? 'border-primary text-primary bg-primary/10'
                          : 'border-muted-foreground/30 text-muted-foreground'
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <StepIcon className="h-5 w-5" />
                      )}
                    </div>
                    <span
                      className={cn(
                        'text-xs mt-1 font-medium',
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      {step.name}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={cn(
                        'flex-1 h-0.5 mx-2',
                        step.id < currentStep ? 'bg-primary' : 'bg-muted-foreground/30'
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Step Content */}
      <main className="container py-6 max-w-2xl">
        {currentStep === 1 && (
          <QuickSetupForm
            contractorId={contractorId}
            userId={userId}
            onComplete={handleSetupComplete}
          />
        )}

        {currentStep === 2 && draftJob && (
          <DocumentDropZone
            jobId={draftJob.jobId}
            contractorId={contractorId}
            permitType={draftJob.permitType}
            existingDocuments={uploadedDocuments}
            onComplete={handleDocumentsComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === 3 && draftJob && (
          <VerificationResults
            jobId={draftJob.jobId}
            contractorId={contractorId}
            draftJob={draftJob}
            documents={uploadedDocuments}
            onAddDocuments={() => setCurrentStep(2)}
          />
        )}
      </main>
    </div>
  );
}
